import type { PageHelp } from '../../help/types';

/** Page guide for `/glossary` — global and project terms the AI must respect (spec §7.12, §9.3, §9.5). */
export const glossaryHelp: PageHelp = {
  title: 'Glossary',
  intro:
    'The words the AI must get right. Global terms are house style for every project; project terms belong to one application. A term with a translation forces that wording, and a do-not-translate term stays in the source language.',
  sections: ['Choosing the list', 'The terms', 'New term dialog', 'Edit term dialog'],
  contexts: {
    'new-term': 'Click “New term” at the top right.',
    'edit-term': 'Click “Edit” on a term in the list.',
  },
  tips: [
    'Every AI translation request carries the global terms plus the terms of the project it runs in, so the model cannot pick a synonym for an agreed word.',
    'Terms only steer future AI translations. Values already stored are not changed when a term is added, edited or removed.',
  ],
  steps: [
    {
      selector: '[data-help="glossary-scope"]',
      title: 'Choose global or project terms',
      body: 'Global terms apply to every project; project terms only to the project currently chosen in the project switcher. The project option is disabled until a project is chosen there.',
      section: 'Choosing the list',
      i18n: {
        'de-AT': {
          title: 'Globale oder Projektbegriffe wählen',
          body: 'Globale Begriffe gelten für jedes Projekt, Projektbegriffe nur für das Projekt, das gerade in der Projektauswahl gewählt ist. Die Projektoption ist gesperrt, bis dort ein Projekt gewählt ist.',
        },
        'sr-Latn-RS': {
          title: 'Izbor globalnih ili projektnih termina',
          body: 'Globalni termini važe za svaki projekat, a projektni samo za projekat koji je trenutno izabran u biraču projekta. Opcija za projekat je zaključana dok se tamo ne izabere projekat.',
        },
      },
    },
    {
      selector: '[data-help="glossary-create"]',
      title: 'Add a term',
      body: 'Opens the New term dialog for the list chosen in Scope. For global terms it is enabled for administrators only, for project terms for the project’s owners and administrators; without that right the page says why above the list.',
      section: 'Choosing the list',
      i18n: {
        'de-AT': {
          title: 'Begriff hinzufügen',
          body: 'Öffnet den Dialog für einen neuen Begriff in der Liste, die unter „Scope“ gewählt ist. Bei globalen Begriffen ist die Schaltfläche nur für Administratoren aktiv, bei Projektbegriffen für Owner des Projekts und Administratoren; ohne dieses Recht nennt die Seite über der Liste den Grund.',
        },
        'sr-Latn-RS': {
          title: 'Dodavanje termina',
          body: 'Otvara dijalog za novi termin u listi izabranoj pod „Scope“. Za globalne termine dugme je aktivno samo za administratore, a za projektne za ownere projekta i administratore; bez tog prava stranica iznad liste navodi razlog.',
        },
      },
    },
    {
      selector: '[data-help="glossary-list"]',
      title: 'Read the terms',
      body: 'Each row is one term in the source language. A “do not translate” chip marks a name the AI must leave as it is; otherwise the chips list the required translation per locale. The note says why the wording was chosen and is sent to the AI with the term.',
      section: 'The terms',
      i18n: {
        'de-AT': {
          title: 'Begriffe lesen',
          body: 'Jede Zeile ist ein Begriff in der Ausgangssprache. Der Chip „do not translate“ kennzeichnet einen Namen, den die KI unverändert lassen muss; sonst zeigen die Chips die vorgeschriebene Übersetzung pro Sprache. Die Notiz sagt, warum die Formulierung gewählt wurde, und geht mit dem Begriff an die KI.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje termina',
          body: 'Svaki red je jedan termin na izvornom jeziku. Čip „do not translate“ označava naziv koji AI mora da ostavi kakav jeste; u suprotnom čipovi navode obavezan prevod po jeziku. Napomena kaže zašto je izabrana ta formulacija i šalje se AI-ju zajedno sa terminom.',
        },
      },
    },
    {
      selector: '[data-help="glossary-edit"]',
      title: 'Edit a term',
      body: 'Opens the term with its note, the do-not-translate switch and its translation fields. Enabled under the same rule as “New term”.',
      section: 'The terms',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Begriff bearbeiten',
          body: 'Öffnet den Begriff mit seiner Notiz, dem Schalter für „do not translate“ und seinen Übersetzungsfeldern. Aktiv nach derselben Regel wie „New term“.',
        },
        'sr-Latn-RS': {
          title: 'Izmena termina',
          body: 'Otvara termin sa napomenom, prekidačem za „do not translate“ i njegovim poljima za prevod. Aktivno je po istom pravilu kao „New term“.',
        },
      },
    },
    {
      selector: '[data-help="glossary-delete"]',
      title: 'Remove a term',
      body: 'Asks for confirmation, then deletes the term with its translations. Future AI translations stop being constrained by it; values already translated are untouched.',
      section: 'The terms',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Begriff entfernen',
          body: 'Fragt nach einer Bestätigung und löscht dann den Begriff samt seinen Übersetzungen. Künftige KI-Übersetzungen sind nicht mehr an ihn gebunden; bereits übersetzte Werte bleiben unverändert.',
        },
        'sr-Latn-RS': {
          title: 'Uklanjanje termina',
          body: 'Traži potvrdu, a zatim briše termin zajedno sa njegovim prevodima. Budući AI prevodi više nisu vezani za njega; već prevedene vrednosti ostaju netaknute.',
        },
      },
    },
    {
      selector: '[data-help="glossary-new-term"]',
      title: 'Enter the term',
      body: 'The word or phrase as it appears in the source language. Required, and the same term cannot be added twice to the same list.',
      section: 'New term dialog',
      context: 'new-term',
      i18n: {
        'de-AT': {
          title: 'Begriff eingeben',
          body: 'Das Wort oder die Wendung so, wie sie in der Ausgangssprache vorkommt. Pflichtfeld; derselbe Begriff kann nicht zweimal in dieselbe Liste aufgenommen werden.',
        },
        'sr-Latn-RS': {
          title: 'Unos termina',
          body: 'Reč ili izraz onako kako se pojavljuje na izvornom jeziku. Obavezno polje; isti termin ne može se dva puta dodati u istu listu.',
        },
      },
    },
    {
      selector: '[data-help="glossary-new-dnt"]',
      title: 'Mark it as do not translate',
      body: 'For brand and product names. The AI leaves the term exactly as it is, and its QA check no longer reports a value that is just this term as an untranslated copy. Turning it on hides the translation fields, and nothing typed into them is saved.',
      section: 'New term dialog',
      context: 'new-term',
      i18n: {
        'de-AT': {
          title: 'Als „do not translate“ markieren',
          body: 'Für Marken- und Produktnamen. Die KI lässt den Begriff genau so, wie er ist, und ihre QA-Prüfung meldet einen Wert, der nur aus diesem Begriff besteht, nicht mehr als unübersetzte Kopie. Eingeschaltet blendet er die Übersetzungsfelder aus, und darin Eingetipptes wird nicht gespeichert.',
        },
        'sr-Latn-RS': {
          title: 'Označavanje kao „do not translate“',
          body: 'Za nazive brendova i proizvoda. AI ostavlja termin tačno onakav kakav jeste, a njegova QA provera više ne prijavljuje vrednost koja se sastoji samo od tog termina kao neprevedenu kopiju. Kada ga uključite, polja za prevod se sakrivaju i ništa što je u njih upisano se ne čuva.',
        },
      },
    },
    {
      selector: '[data-help="glossary-new-locale"]',
      title: 'Pick the locale',
      body: 'The locale of the required translation, shown while do not translate is off. For a global term the list holds every active locale; for a project term, the locales of that project.',
      section: 'New term dialog',
      context: 'new-term',
      i18n: {
        'de-AT': {
          title: 'Sprache wählen',
          body: 'Die Sprache der vorgeschriebenen Übersetzung, sichtbar solange „do not translate“ ausgeschaltet ist. Bei einem globalen Begriff enthält die Liste jede aktive Sprache, bei einem Projektbegriff die Sprachen dieses Projekts.',
        },
        'sr-Latn-RS': {
          title: 'Izbor jezika',
          body: 'Jezik obaveznog prevoda, prikazan dok je „do not translate“ isključeno. Za globalni termin lista sadrži svaki aktivni jezik, a za projektni termin jezike tog projekta.',
        },
      },
    },
    {
      selector: '[data-help="glossary-new-translation"]',
      title: 'Give the required translation',
      body: 'The exact wording the AI must use for this term in the chosen locale. Optional here and only saved together with a locale: the dialog takes one, and more are added afterwards with “Edit”.',
      section: 'New term dialog',
      context: 'new-term',
      i18n: {
        'de-AT': {
          title: 'Vorgeschriebene Übersetzung angeben',
          body: 'Die genaue Formulierung, die die KI für diesen Begriff in der gewählten Sprache verwenden muss. Hier optional und nur zusammen mit einer Sprache gespeichert: Der Dialog nimmt eine auf, weitere fügen Sie danach mit „Edit“ hinzu.',
        },
        'sr-Latn-RS': {
          title: 'Unos obaveznog prevoda',
          body: 'Tačna formulacija koju AI mora da koristi za ovaj termin na izabranom jeziku. Ovde je opciona i čuva se samo zajedno sa jezikom: dijalog prima jedan, a ostale dodajte posle toga preko „Edit“.',
        },
      },
    },
    {
      selector: '[data-help="glossary-new-note"]',
      title: 'Add a note',
      body: 'Optional: why this wording was chosen, or which meaning of the term is intended. It is sent to the AI with the term and appears in the Note column of the list.',
      section: 'New term dialog',
      context: 'new-term',
      i18n: {
        'de-AT': {
          title: 'Notiz hinzufügen',
          body: 'Optional: warum diese Formulierung gewählt wurde oder welche Bedeutung des Begriffs gemeint ist. Sie geht mit dem Begriff an die KI und erscheint in der Spalte „Note“ der Liste.',
        },
        'sr-Latn-RS': {
          title: 'Dodavanje napomene',
          body: 'Opciono: zašto je izabrana ova formulacija ili koje značenje termina je namenjeno. Šalje se AI-ju zajedno sa terminom i prikazuje u koloni „Note“ u listi.',
        },
      },
    },
    {
      selector: '[data-help="glossary-new-submit"]',
      title: 'Add the term',
      body: 'Saves the term to the list chosen in Scope; the dialog title says which, global or project. Enabled once the term is filled in, and a duplicate term or a missing right is reported in the dialog with nothing saved. Cancel discards what was typed.',
      section: 'New term dialog',
      context: 'new-term',
      i18n: {
        'de-AT': {
          title: 'Begriff speichern',
          body: 'Speichert den Begriff in der unter „Scope“ gewählten Liste; der Dialogtitel sagt, ob global oder Projekt. Aktiv, sobald der Begriff ausgefüllt ist; ein doppelter Begriff oder ein fehlendes Recht wird im Dialog gemeldet, und nichts wird gespeichert. „Cancel“ verwirft die Eingaben.',
        },
        'sr-Latn-RS': {
          title: 'Čuvanje termina',
          body: 'Čuva termin u listi izabranoj pod „Scope“; naslov dijaloga kaže da li je globalna ili projektna. Aktivno je čim je termin unet, a duplikat ili nedostatak prava prijavljuje se u dijalogu i ništa se ne čuva. „Cancel“ odbacuje uneto.',
        },
      },
    },
    {
      selector: '[data-help="glossary-edit-term"]',
      title: 'Correct the term',
      body: 'Changes the source-language wording of the term. It cannot be empty: “Save” stays disabled until the field is filled in.',
      section: 'Edit term dialog',
      context: 'edit-term',
      i18n: {
        'de-AT': {
          title: 'Begriff korrigieren',
          body: 'Ändert die Formulierung des Begriffs in der Ausgangssprache. Er darf nicht leer sein: „Save“ bleibt gesperrt, bis das Feld ausgefüllt ist.',
        },
        'sr-Latn-RS': {
          title: 'Ispravka termina',
          body: 'Menja formulaciju termina na izvornom jeziku. Ne sme biti prazan: „Save“ ostaje zaključano dok se polje ne popuni.',
        },
      },
    },
    {
      selector: '[data-help="glossary-edit-dnt"]',
      title: 'Switch do not translate',
      body: 'On, the AI leaves the term as it is and the translation fields are disabled; the translations already stored are kept. Off, the translations below apply again.',
      section: 'Edit term dialog',
      context: 'edit-term',
      i18n: {
        'de-AT': {
          title: '„Do not translate“ umschalten',
          body: 'Eingeschaltet lässt die KI den Begriff unverändert und die Übersetzungsfelder sind gesperrt; bereits gespeicherte Übersetzungen bleiben erhalten. Ausgeschaltet gelten die Übersetzungen darunter wieder.',
        },
        'sr-Latn-RS': {
          title: 'Uključivanje „do not translate“',
          body: 'Kada je uključeno, AI ostavlja termin kakav jeste, a polja za prevod su zaključana; već sačuvani prevodi se zadržavaju. Kada je isključeno, prevodi ispod ponovo važe.',
        },
      },
    },
    {
      selector: '[data-help="glossary-edit-note"]',
      title: 'Edit the note',
      body: 'Why this wording was chosen, or which meaning of the term is intended; sent to the AI with the term. Empty the field to remove the note.',
      section: 'Edit term dialog',
      context: 'edit-term',
      i18n: {
        'de-AT': {
          title: 'Notiz bearbeiten',
          body: 'Warum diese Formulierung gewählt wurde oder welche Bedeutung des Begriffs gemeint ist; geht mit dem Begriff an die KI. Leeren Sie das Feld, um die Notiz zu entfernen.',
        },
        'sr-Latn-RS': {
          title: 'Izmena napomene',
          body: 'Zašto je izabrana ova formulacija ili koje značenje termina je namenjeno; šalje se AI-ju zajedno sa terminom. Ispraznite polje da biste uklonili napomenu.',
        },
      },
    },
    {
      selector: '[data-help="glossary-edit-translation"]',
      title: 'Set the translation per locale',
      body: 'One field per locale: every active locale for a global term, the project’s locales for a project term, plus any locale the term already has a translation in. Saving replaces the term’s translations as a whole, so a field you empty removes that locale’s translation.',
      section: 'Edit term dialog',
      context: 'edit-term',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Übersetzung pro Sprache festlegen',
          body: 'Ein Feld pro Sprache: jede aktive Sprache bei einem globalen Begriff, die Sprachen des Projekts bei einem Projektbegriff, dazu jede Sprache, in der der Begriff bereits eine Übersetzung hat. Beim Speichern werden die Übersetzungen des Begriffs als Ganzes ersetzt, ein geleertes Feld entfernt also die Übersetzung dieser Sprache.',
        },
        'sr-Latn-RS': {
          title: 'Prevod po jeziku',
          body: 'Po jedno polje za svaki jezik: svaki aktivni jezik za globalni termin, jezici projekta za projektni termin, plus svaki jezik na kome termin već ima prevod. Čuvanje zamenjuje prevode termina u celini, pa polje koje ispraznite uklanja prevod za taj jezik.',
        },
      },
    },
    {
      selector: '[data-help="glossary-edit-save"]',
      title: 'Save the term',
      body: 'Saves the term, the switch and the note, then replaces the translations with what the fields hold. An error from the server is shown in the dialog, and the dialog stays open.',
      section: 'Edit term dialog',
      context: 'edit-term',
      i18n: {
        'de-AT': {
          title: 'Begriff speichern',
          body: 'Speichert Begriff, Schalter und Notiz und ersetzt dann die Übersetzungen durch den Inhalt der Felder. Ein Fehler des Servers wird im Dialog angezeigt, und der Dialog bleibt offen.',
        },
        'sr-Latn-RS': {
          title: 'Čuvanje termina',
          body: 'Čuva termin, prekidač i napomenu, a zatim zamenjuje prevode sadržajem polja. Greška sa servera prikazuje se u dijalogu, a dijalog ostaje otvoren.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Glossar',
      intro:
        'Die Wörter, die die KI richtig treffen muss. Globale Begriffe sind der Hausstil für jedes Projekt, Projektbegriffe gehören zu einer Anwendung. Ein Begriff mit Übersetzung erzwingt diese Formulierung, ein „do not translate“-Begriff bleibt in der Ausgangssprache.',
      sections: {
        'Choosing the list': 'Liste wählen',
        'The terms': 'Die Begriffe',
        'New term dialog': 'Dialog „New term“',
        'Edit term dialog': 'Dialog „Edit“',
      },
      contexts: {
        'new-term': 'Klicken Sie rechts oben auf „New term“.',
        'edit-term': 'Klicken Sie bei einem Begriff in der Liste auf „Edit“.',
      },
      tips: [
        'Jede KI-Übersetzungsanfrage enthält die globalen Begriffe und die Begriffe des Projekts, in dem sie läuft; das Modell kann für ein vereinbartes Wort also kein Synonym wählen.',
        'Begriffe steuern nur künftige KI-Übersetzungen. Bereits gespeicherte Werte ändern sich nicht, wenn ein Begriff hinzugefügt, bearbeitet oder entfernt wird.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Glosar',
      intro:
        'Reči koje AI mora da pogodi. Globalni termini su kućni stil za svaki projekat, a projektni pripadaju jednoj aplikaciji. Termin sa prevodom nameće tu formulaciju, a termin „do not translate“ ostaje na izvornom jeziku.',
      sections: {
        'Choosing the list': 'Izbor liste',
        'The terms': 'Termini',
        'New term dialog': 'Dijalog „New term“',
        'Edit term dialog': 'Dijalog „Edit“',
      },
      contexts: {
        'new-term': 'Kliknite na „New term“ gore desno.',
        'edit-term': 'Kliknite na „Edit“ kod termina u listi.',
      },
      tips: [
        'Svaki zahtev za AI prevod nosi globalne termine i termine projekta u kome se izvršava, pa model ne može da izabere sinonim za dogovorenu reč.',
        'Termini usmeravaju samo buduće AI prevode. Već sačuvane vrednosti se ne menjaju kada se termin doda, izmeni ili ukloni.',
      ],
    },
  },
};
